/**
 * Weekday × hour heatmap. Cell intensity is the share of the busiest
 * cell; hovering or tapping a cell prints its label and count in a
 * readout line under the grid (a floating tooltip was clipped by the
 * scroll container on phones). The last touched cell stays selected.
 */

import { useState } from 'react';

import { useC } from '../../lib/theme.js';

const MONO = "'JetBrains Mono', monospace";
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export interface HeatmapProps {
  /** rows[weekday 0=Mon..6][hour 0..23] */
  readonly rows: ReadonlyArray<readonly number[]>;
  readonly formatValue?: ((n: number) => string) | undefined;
  readonly unit?: string | undefined;
  /** Hex colour of the hottest cell (alpha is applied by intensity). */
  readonly color?: string | undefined;
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (m === null) return [217, 255, 63];
  return [parseInt(m[1]!, 16), parseInt(m[2]!, 16), parseInt(m[3]!, 16)];
}

/** Default count formatter: thousands separators (3,455 not 3455). */
const formatCount = (n: number): string => n.toLocaleString('en-US');

export function Heatmap({ rows, formatValue = formatCount, unit = 'page loads', color }: HeatmapProps): JSX.Element {
  const C = useC();
  const [hover, setHover] = useState<{ wd: number; h: number } | null>(null);
  const [r, g, b] = hexToRgb(color ?? C.limeFg);
  const max = Math.max(1, ...rows.flatMap((row) => row.map((v) => v)));
  const hovered = hover !== null ? rows[hover.wd]?.[hover.h] ?? 0 : null;

  return (
    <div style={{ position: 'relative', userSelect: 'none' }} data-testid="heatmap">
      <div style={{ display: 'grid', gridTemplateColumns: '30px repeat(24, minmax(0, 1fr))', gap: 1.5, alignItems: 'center' }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} style={{ fontFamily: MONO, fontSize: 8, color: C.dim, textAlign: 'center', letterSpacing: 0.5 }}>{h % 6 === 0 ? String(h).padStart(2, '0') : ''}</div>
        ))}
        {WEEKDAYS.map((label, wd) => (
          <div key={label} style={{ display: 'contents' }}>
            <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1 }}>{label.toUpperCase()}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const v = rows[wd]?.[h] ?? 0;
              const a = v === 0 ? 0 : 0.15 + 0.85 * (v / max);
              const on = hover?.wd === wd && hover.h === h;
              return (
                <div
                  key={h}
                  data-cell={`${wd}-${h}`}
                  onMouseEnter={() => setHover({ wd, h })}
                  onClick={() => setHover({ wd, h })}
                  style={{
                    aspectRatio: '1 / 1',
                    minHeight: 10,
                    borderRadius: 1,
                    background: v === 0 ? C.surface2 : `rgba(${r},${g},${b},${a.toFixed(2)})`,
                    outline: on ? `1px solid ${C.text}` : 'none',
                    outlineOffset: -1,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div
        role="status"
        aria-live="polite"
        data-testid="heatmap-readout"
        style={{ marginTop: 8, fontFamily: MONO, fontSize: 10, letterSpacing: 1, color: hover === null ? C.dim : C.text, minHeight: 16 }}
      >
        {hover !== null && hovered !== null
          ? `${WEEKDAYS[hover.wd]} · ${String(hover.h).padStart(2, '0')}:00–${String(hover.h).padStart(2, '0')}:59 UTC · ${formatValue(hovered)} ${unit}`
          : 'HOVER OR TAP A CELL'}
      </div>
    </div>
  );
}
